import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { describeVariabilityType } from '../utils/variabilityTypes';

/** "8.6 V" + "13.9 V" -> "8.6–13.9 V" (shared band collapsed); falls back gracefully. */
function formatMagRange(maxMag, minMag) {
  if (!maxMag && !minMag) return null;
  if (!maxMag || !minMag) return (maxMag || minMag);
  const m = /^(\S+)\s+(\S+)$/.exec(maxMag);
  const n = /^(\S+)\s+(\S+)$/.exec(minMag);
  if (m && n && m[2] === n[2]) return `${m[1]}–${n[1]} ${m[2]}`; // same band
  return `${maxMag} – ${minMag}`;
}

function formatPeriod(days) {
  const p = Number(days);
  if (!isFinite(p)) return null;
  if (p < 1) return `${p.toPrecision(3)} d`;
  return `${p >= 100 ? Math.round(p) : p.toFixed(2)} d`;
}

/**
 * Compact display of VSX variable-star metadata attached to a resolved target:
 * variability type, brightness range (max→min), and period. Renders nothing when
 * the object isn't a known variable.
 */
export default function VariableStarInfo({ variableStar, sx }) {
  const vs = variableStar;
  if (!vs) return null;
  const magRange = formatMagRange(vs.maxMag, vs.minMag);
  const period = vs.period != null ? formatPeriod(vs.period) : null;
  const typeDesc = describeVariabilityType(vs.type);
  const chips = [];
  if (typeDesc) {
    // Human label on the chip; raw VSX code (+ decode) in the tooltip.
    const label = typeDesc.uncertain ? `${typeDesc.short}?` : typeDesc.short;
    chips.push(['type', label, typeDesc.full || `VSX type ${vs.type}`]);
  }
  if (magRange) chips.push(['mag', magRange, 'Brightness range (bright → faint)']);
  if (period) chips.push(['period', period, 'Period']);
  if (chips.length === 0 && !vs.auid) return null;
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', ...sx }}>
      {chips.map(([key, label, title]) => (
        <Tooltip key={key} title={title} arrow>
          <Chip size="small" variant="outlined" label={label} sx={{ height: 20, fontSize: '0.68rem' }} />
        </Tooltip>
      ))}
      {vs.auid && (
        <Typography variant="caption" color="text.secondary">AUID {vs.auid}</Typography>
      )}
    </Box>
  );
}
